import { Injectable } from '@angular/core';
import { combineQueries, getIDType } from '@datorama/akita';
import { AclAssetPermission } from '@y42/acl/models';
import {
  AllDatasourcesQuery,
  AllDatasourceTablesQuery,
  DatasourceTypeQuery,
  UNKNOWN_DATASOURCE_TYPE_MODEL,
} from '@y42/datasource';
import { AbstractFeatureQueryEntity, Y42FeatureToAclAssetResourceType } from '@y42/feature';
import { FeatureAclQuery } from '@y42/feature/acl';
import { Y42FeatureType } from '@y42/json-schema';
import { assertNonNullable } from '@y42/util/assert';
import { combineLatest, map, Observable } from 'rxjs';
import { AvailableAllChartType } from '../../models/widget/chart-type-constant.model';
import { VisualizationWidgetListViewData } from './visualization-widget-list-view-data.model';
import { VisualizationWidgetState } from './visualization-widget-state.model';
import { VisualizationWidgetStoreModel, VisualizationWidgetUIModel } from './visualization-widget.model';
import { VisualizationWidgetStore } from './visualization-widget.store';

@Injectable({ providedIn: 'root' })
export class VisualizationWidgetQuery extends AbstractFeatureQueryEntity<
  VisualizationWidgetState,
  VisualizationWidgetUIModel
> {
  constructor(
    protected readonly store: VisualizationWidgetStore,
    private readonly featureAclQuery: FeatureAclQuery,
    private readonly allDatasourcesQuery: AllDatasourcesQuery,
    private readonly allDatasourceTablesQuery: AllDatasourceTablesQuery,
    private readonly datasourceTypeQuery: DatasourceTypeQuery,
  ) {
    super({
      store,
      featureType: Y42FeatureType.VisualizationWidget,
      toUIModel: (storeModel) => {
        const { last_used, starred } = this.getValue();

        const resourceType = Y42FeatureToAclAssetResourceType[Y42FeatureType.VisualizationWidget];
        assertNonNullable(resourceType);

        return {
          featureType: Y42FeatureType.VisualizationWidget,
          ...storeModel,
          ...storeModel.settings,
          last_used: last_used[storeModel.$path] || null,
          starred: starred[storeModel.$path],
          hasReadPermission: !!this.featureAclQuery.getPermission({
            resourceType,
            assetPath: storeModel.$path,
            permission: AclAssetPermission.Read,
          })?.allowed,
        };
      },
    });
  }

  readonly active$: Observable<VisualizationWidgetUIModel | undefined> = combineLatest([
    this.selectActive(),
    this.select((st) => st.last_used),
    this.select((st) => st.starred),
  ]).pipe(map(([active]) => (active ? this.toUIModel(active) : undefined)));

  readonly items$: Observable<VisualizationWidgetUIModel[]> = combineLatest([
    this.selectAll(),
    this.select((st) => st.last_used),
    this.select((st) => st.starred),
  ]).pipe(map(([models]) => models.map((model) => this.toUIModel(model))));

  readonly listItems$: Observable<VisualizationWidgetListViewData[]> = combineQueries([
    this.items$,
    this.allDatasourcesQuery.selectAll(),
    this.allDatasourceTablesQuery.selectAll(),
  ]).pipe(
    map(([items]) => items.map(this.toListViewModel)),
    map((arr) => arr.flat()),
  );

  getUniqueNameForCopy(original: VisualizationWidgetUIModel) {
    const originalName = original.$name.replace(/- Copy \d*$/, '');

    let index = 1;

    while (this.getEntityByName(`${originalName} - Copy ${index}`)) {
      index++;
    }

    return `${originalName} - Copy ${index}`;
  }

  getItem(id: VisualizationWidgetStoreModel['$path']): VisualizationWidgetUIModel<AvailableAllChartType> | undefined {
    return this.getUIEntity(id);
  }

  getWidgetSettings(id: getIDType<VisualizationWidgetState>) {
    return this.getEntity(id)?.settings;
  }

  readonly toListViewModel = (item: VisualizationWidgetUIModel): VisualizationWidgetListViewData[] => {
    const datasourceTable = item.datasource_table_path
      ? this.allDatasourceTablesQuery.getUIEntity(item.datasource_table_path)
      : undefined;

    const datasource = datasourceTable ? this.allDatasourcesQuery.getEntity(datasourceTable.$parent) : undefined;
    const lastValidJobDate = datasourceTable?.latest_valid_job?.finished_at;
    const datasourceTypeModel =
      (datasource && this.datasourceTypeQuery.findModelBySchema(datasource.settings.$schema)) ??
      UNKNOWN_DATASOURCE_TYPE_MODEL;

    const listItem: VisualizationWidgetListViewData = {
      ...item,
      id: item.$path,
      datasourceTableName: datasourceTable?.name ?? '',
      datasourceTypeModel,
      lastValidJobDate: lastValidJobDate ? new Date(lastValidJobDate) : undefined,
      tabs: 0,
    };

    return [listItem];
  };
}
